import {
  useGetAcceptedLeadsQuery,
  useLazyGetAcceptedLeadsQuery,
  LEADS_PAGE_SIZE,
  LEADS_EXPORT_MAX,
} from '@/services/leads';
import { LeadTableRow } from '@/types/Leads';
import { Box, Card, Icon, Pagination, toast } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { LeadManagementFilter } from './LeadManagementFilter';
import { LeadManagementLoading } from './LeadManagementLoading';
import { LeadManagementTable } from './LeadManagementTable';
import { useLeadTabParams } from './useLeadTabParams';

const primaryColorStyle: React.CSSProperties = { color: 'var(--btn-bg-primary-idle)' };
const prevPageIcon = <Icon name="caret-left" size="sm" style={primaryColorStyle} />;
const nextPageIcon = <Icon name="caret-right" size="sm" style={primaryColorStyle} />;

function escapeCsvCell(value: string): string {
  if (value.length > 0 && (value[0] === '=' || value[0] === '+' || value[0] === '-' || value[0] === '@')) {
    value = '\t' + value;
  }
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function exportLeadsToCsv(leads: LeadTableRow[]): void {
  const headers = [
    'Account Number',
    'Location',
    'System Size',
    'Estimated Year 1 Production',
    'Battery Size',
    'Price Per Watt',
    'Date Accepted',
  ];

  const rows = leads.map((lead) => [
    escapeCsvCell(lead.id),
    escapeCsvCell(`${lead.address.locality}, ${lead.address.region} ${lead.address.postalCode}`),
    escapeCsvCell(`${lead.metrics.systemSize} kW`),
    escapeCsvCell(lead.metrics.firstYearProduction != null ? `${lead.metrics.firstYearProduction} kWh` : ''),
    escapeCsvCell(lead.storage != null ? `${lead.storage.capacity} kWh` : ''),
    escapeCsvCell(`$${lead.pricing.ppw.toFixed(2)}`),
    escapeCsvCell(DateTime.fromISO(lead.meta.updatedAt).toFormat('MM-dd-yyyy')),
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', `accepted_leads_${DateTime.now().toFormat('yyyy-MM-dd')}.csv`);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function GoToAccountLink({ row }: { row: LeadTableRow }) {
  return (
    <Box display="block" fontWeight="medium" fontSize="sm">
      <Link to={`/accounts/${row.id}`}> Go to account</Link>
    </Box>
  );
}

export function LeadManagementAcceptedTab() {
  const {
    queryArgs,
    page,
    searchTerm,
    appliedSort,
    activeFilterCount,
    currentFilters,
    handleSortSelection,
    handlePageChange,
    handleSearchChange,
    handleApplyFilters,
  } = useLeadTabParams();
  const { data, isLoading } = useGetAcceptedLeadsQuery(queryArgs);
  const leads = useMemo(() => data?.leads ?? [], [data?.leads]);

  const [fetchAllLeads] = useLazyGetAcceptedLeadsQuery();
  const [isExporting, setIsExporting] = useState(false);
  const renderActions = useCallback((row: LeadTableRow) => <GoToAccountLink row={row} />, []);
  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { leads: allLeads } = await fetchAllLeads({
        ...queryArgs,
        pageNum: 1,
        pageSize: LEADS_EXPORT_MAX,
      }).unwrap();
      exportLeadsToCsv(allLeads);
    } catch {
      toast.error('Failed to export leads. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllLeads, isExporting, queryArgs]);

  return (
    <Card padding="xs" marginTop="0">
      <Card.Header>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Box fontSize="xl" fontWeight="medium">
            Accepted
          </Box>
        </Box>
      </Card.Header>
      <Box margin="0 lg lg lg" direction="column" childGap="md">
        <LeadManagementFilter
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          activeFilterCount={activeFilterCount}
          currentFilters={currentFilters}
          onApplyFilters={handleApplyFilters}
          appliedSort={appliedSort}
          onSortChange={handleSortSelection}
          onExport={handleExport}
          isExporting={isExporting}
        />
        <Box overflow="hidden" radius="md">
          {isLoading ? (
            <Box padding="md">
              <LeadManagementLoading />
            </Box>
          ) : (
            <LeadManagementTable leads={leads} statusDateLabel="Date Accepted" renderActions={renderActions} />
          )}
        </Box>
        {(data?.total ?? 0) > LEADS_PAGE_SIZE && (
          <Pagination
            activePage={page}
            arePagesVisible
            itemsPerPage={LEADS_PAGE_SIZE}
            numberOfPagesDisplayed={3}
            onChange={handlePageChange}
            totalItemsCount={data?.total ?? 0}
            isCompact
            prevPageText={prevPageIcon}
            nextPageText={nextPageIcon}
          />
        )}
      </Box>
    </Card>
  );
}
